import { isPlatformBrowser } from '@angular/common';
import {
  Injectable,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, pairwise, tap } from 'rxjs';
import {
  DEFAULT_LANG,
  TEL_LANG_MAP,
  MAIL_LANG_MAP,
  APP_LANGS,
} from './language.constants';
import { Langs } from './language.types';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translate = inject(TranslateService);
  private readonly platformId = inject<Object>(PLATFORM_ID);
  private readonly router = inject(Router);

  public readonly activeLanguage = signal<Langs>(DEFAULT_LANG);
  public readonly langPath = computed(() => `/${this.activeLanguage()}/`);
  public readonly langTelNum = computed(
    () => TEL_LANG_MAP[this.activeLanguage()] ?? TEL_LANG_MAP['en'],
  );
  public readonly langEmail = computed(
    () => MAIL_LANG_MAP[this.activeLanguage()] ?? MAIL_LANG_MAP['en'],
  );

  public initLanguage() {
    this.translate.addLangs([...APP_LANGS]);
    this.translate.setDefaultLang(DEFAULT_LANG);
    const initLang = this.getInitLang();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => e.urlAfterRedirects.substring(1).split('/')[1]),
        pairwise(),
        tap(([prev, next]) => {
          if (prev !== next && isPlatformBrowser(this.platformId)) {
            window.scrollTo({ top: 0 });
          }
        }),
      )
      .subscribe();

    return this.setLanguage(initLang);
  }

  getInitLang(): Langs {
    const langStorage = isPlatformBrowser(this.platformId)
      ? (localStorage.getItem('language') as Langs | null)
      : null;

    const langBrowser = this.translate
      .getBrowserCultureLang()
      ?.slice(0, 2)
      .toLowerCase() as Langs | undefined;

    const language = langStorage ?? langBrowser;
    return language && APP_LANGS.includes(language) ? language : DEFAULT_LANG;
  }

  setLanguage(lang: Langs) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('language', lang);
    }
    this.activeLanguage.set(lang);
    return this.translate.use(lang);
  }

  private hasTranslation(key: string): string | false {
    const translation = this.translate.instant(key);
    return translation !== key && translation !== '' ? translation : false;
  }

  instant(key: string, returnOnKeyNotExists?: string): string;
  instant(key: string, returnOnKeyNotExists: string | null): string | null;
  instant(
    key: string,
    returnOnKeyNotExists: string | null = 'Missing translation',
  ): string | null {
    const translation = this.hasTranslation(key);
    if (translation === false) {
      return returnOnKeyNotExists;
    }
    return translation;
  }
}
